import axios, { AxiosResponse } from '../../node_modules/axios/index';
import { getApiUrl } from '../constants/api';
import { Product } from '../types/product';
import { handleCurrencyChange, getTaxWithRetry } from '../utils/calculate-tax';

interface SearchProductsParams {
  query?: string;
  limit?: number;
  orderBy?: string;
  orderDir?: string;
  storeId: string;
  storeType: string;
  filters?: FilterRequest;
  page?: number;
  currency?: string;
  signal?: AbortSignal;
  image?: string;
  language?: string;
}

interface SearchProductsResponse {
  results: {
    products: Product[];
    filters: Filter[];
  };
  meta: {
    pageNumber: number;
    pageSize: number;
    prevPage?: number;
    nextPage?: number;
    total: number;
    totalPages: number;
  };
  tax?: number;
}

interface FilterValue {
  value: string;
  count: number;
}

export interface Filter {
  key: string;
  values?: FilterValue[]; // Optional, as some filters (like "price") don't have values
  min?: number; // Optional, used for price ranges
  max?: number; // Optional, used for price ranges
}

export interface FilterRequest {
  filters: (
    | {
        key: string;
        from: string;
        to: string;
      }
    | {
        key: string;
        values: string[];
      }
  )[];
}

export async function searchProducts(params: SearchProductsParams) {
  console.log('searchProducts called with params:', {
    storeId: params.storeId,
    currency: params.currency,
  });

  const {
    storeId,
    storeType,
    query,
    limit,
    filters,
    orderBy,
    orderDir,
    page = 1,
    image,
    language,
  } = params;
  const response: AxiosResponse<SearchProductsResponse> = await axios.post(
    `${getApiUrl(storeId)}/store/search/products/${storeId}/${storeType}?${
      query ? `query=${query}` : ''
    }${limit ? `&limit=${limit}` : ''}${page ? `&page=${page}` : ''}${
      orderBy ? `&orderBy=${orderBy}` : ''
    }${orderDir ? `&orderDir=${orderDir}` : ''}${
      image ? `&image=${image}` : ''
    }${language ? `&lang=${language}` : ''}`,
    filters && filters.filters.length > 0 ? filters : undefined,
    {
      signal: params.signal,
    },
  );
  try {
    if (response.data.results.products.length > 0) {
      // Get currency from the first product
      const currentCurrency = params.currency;
      handleCurrencyChange(currentCurrency as string);

      // Get tax with retry logic
      const tax = await getTaxWithRetry(
        storeType,
        response.data.results.products[0].productId,
        response.data.results.products[0].price,
      );

      // If we couldn't get a valid tax after retries, return original data
      if (tax === null || isNaN(tax) || !isFinite(tax) || tax <= 0) {
        console.warn(
          'Could not calculate valid tax, returning original prices',
        );
        return response.data;
      }

      const updatedProducts = response.data.results.products.map((product) => {
        return {
          ...product,
          price: product.isTaxable ? product.price * tax : product.price,
          discountedPrice: product?.discountedPrice
            ? product.isTaxable
              ? product.discountedPrice * tax
              : product.discountedPrice
            : undefined,
        };
      });

      const updatedFilters = response.data.results.filters.map((filter) => {
        if (filter.key === 'price') {
          return {
            ...filter,
            min: filter?.min ? filter.min * tax : undefined,
            max: filter?.max ? filter.max * tax : undefined,
          };
        }
        return filter;
      });

      return {
        ...response.data,
        tax,
        results: {
          ...response.data.results,
          products: updatedProducts,
          filters: updatedFilters,
        },
      };
    }

    return response.data;
  } catch (error) {
    console.error('Error processing product prices:', error);
    return response.data;
  }
}
